import { useId } from "react";

import { Carousel } from "../../../../core/Carousel.js";
import { CAROUSEL_SLIDES } from "../../../../core/Carousel.styles.js";
import { CreateBoxCard } from "../../../../domain/CreateBoxCard.js";
import { PackParade } from "../../../../moments/PackParade.js";
import { ClosingBand } from "../../../../patterns/ClosingBand.js";
import { Section } from "../../../../patterns/Section.js";
import { SectionHeader } from "../../../../patterns/SectionHeader.js";
import { ForgeCard } from "../../../forge/sections/grid/ForgeGrid.js";
import { ActionButton } from "../../../shared/ActionButton.js";
import { BoxCardSkeleton } from "../../../shared/BoxCards.js";
import type { HomeForgeContent } from "../../homeContent.js";

/**
 * The Forge's pitch: build a box of one's own, with Create and Browse. The `showcase` variant (the
 * default) sets the words over a rail of the boxes shoppers already built, each with who built it,
 * and draws the band while none are built; `stage` sets them at the start of a lit band with the
 * store's boxes turning behind them (a PackParade), Create shining; `band` sets them in its
 * middle, alone.
 */
export function HomeForge({
  variant = "showcase",
  ...forge
}: HomeForgeContent & { variant?: "band" | "stage" | "showcase" }) {
  if (variant === "showcase" && forge.boxes && forge.boxes.cards?.length !== 0) {
    return <ForgeShowcase {...forge} boxes={forge.boxes} />;
  }
  const stage = variant === "stage";
  return (
    <ClosingBand
      title={forge.title}
      body={forge.body}
      arrange={stage ? "start" : "centered"}
      media={
        stage && forge.parade && forge.parade.length > 0 ? (
          <PackParade packs={forge.parade.map(({ image, items }) => ({ image, cards: items }))} />
        ) : undefined
      }
      action={
        <div className="flex flex-wrap justify-center gap-3">
          {forge.actions.map((action) => (
            <ActionButton
              key={`${action.href}${action.label}`}
              action={action}
              variant={action.emphasis === "primary" ? "brand" : "secondary"}
              size="lg"
              shine={stage && action.emphasis === "primary"}
            />
          ))}
        </div>
      }
    />
  );
}

/** How many outlines stand in for the shoppers' boxes while they load. */
const LOADING_SLIDES = 5;

/**
 * The pitch as a section header with Browse as its link, over a rail of the boxes shoppers built
 * led by a card for the one they could build (Create), fanning the store's packs: from the first
 * frame, with outlines beside it while the boxes load.
 */
function ForgeShowcase({
  title,
  body,
  actions,
  parade,
  boxes,
}: HomeForgeContent & { boxes: NonNullable<HomeForgeContent["boxes"]> }) {
  const id = useId();
  const create = actions.find((action) => action.emphasis === "primary");
  const browse = actions.find((action) => action.emphasis !== "primary");
  // The store's packs, as the stage's parade has them, else the shoppers' own boxes.
  const art = (parade?.map((pack) => pack.image) ?? []).concat(
    (boxes.cards ?? []).map((card) => card.box.imageUrl),
  );
  return (
    // The Carousel is the region, named by the title, so the section names nothing.
    <Section aria-busy={boxes.cards === null || undefined}>
      <Carousel
        label={title}
        heading={<SectionHeader title={title} titleId={id} subtitle={body} action={browse} />}
        slideClassName={CAROUSEL_SLIDES.tiles}
      >
        {create && <CreateBoxCard key="create" label={create.label} href={create.href} art={art} />}
        {boxes.cards === null
          ? Array.from({ length: LOADING_SLIDES }, (_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity of their own.
              <BoxCardSkeleton key={i} />
            ))
          : boxes.cards.map((card) => (
              <ForgeCard key={card.box.id} card={card} currency={boxes.currency} />
            ))}
      </Carousel>
    </Section>
  );
}
